/**
 * Handler registry for managing MSW request handlers
 */

import type { RequestHandler } from 'msw'
import type { HandlerRegistry, HandlerMetadata, TaggedHandler } from './types'

/**
 * Implementation of handler registry
 */
export class MockHandlerRegistry implements HandlerRegistry {
  private handlers = new Set<RequestHandler>()
  private taggedHandlers = new Map<string, Set<RequestHandler>>()
  private handlerMetadata = new WeakMap<RequestHandler, HandlerMetadata>()

  /**
   * Register handlers
   */
  register(...handlers: RequestHandler[]): void {
    for (const handler of handlers) {
      this.handlers.add(handler)
    }
  }

  /**
   * Unregister handlers
   */
  unregister(...handlers: RequestHandler[]): void {
    for (const handler of handlers) {
      this.handlers.delete(handler)
      
      // Remove from tagged handlers
      for (const taggedSet of this.taggedHandlers.values()) {
        taggedSet.delete(handler)
      }
      
      // Remove metadata
      this.handlerMetadata.delete(handler)
    }
  }

  /**
   * Get all registered handlers
   */
  getHandlers(): RequestHandler[] {
    return Array.from(this.handlers)
  }

  /**
   * Clear all handlers
   */
  clear(): void {
    this.handlers.clear()
    this.taggedHandlers.clear()
    // WeakMap will be garbage collected automatically
  }

  /**
   * Get handlers by tag
   */
  getByTag(tag: string): RequestHandler[] {
    const taggedSet = this.taggedHandlers.get(tag)
    return taggedSet ? Array.from(taggedSet) : []
  }

  /**
   * Tag handlers
   */
  tag(tag: string, ...handlers: RequestHandler[]): void {
    if (!this.taggedHandlers.has(tag)) {
      this.taggedHandlers.set(tag, new Set())
    }
    
    const taggedSet = this.taggedHandlers.get(tag)!
    
    for (const handler of handlers) {
      taggedSet.add(handler)
      
      // Update metadata
      const existingMetadata = this.handlerMetadata.get(handler) || {}
      const tags = existingMetadata.tags || []
      if (!tags.includes(tag)) {
        tags.push(tag)
      }
      
      this.handlerMetadata.set(handler, {
        ...existingMetadata,
        tags,
      })
    }
  }

  /**
   * Set metadata for a handler
   */
  setMetadata(handler: RequestHandler, metadata: HandlerMetadata): void {
    this.handlerMetadata.set(handler, metadata)
    
    // Add to tagged handlers if tags are specified
    if (metadata.tags) {
      for (const tag of metadata.tags) {
        this.tag(tag, handler)
      }
    }
  }

  /**
   * Get metadata for a handler
   */
  getMetadata(handler: RequestHandler): HandlerMetadata | undefined {
    return this.handlerMetadata.get(handler)
  }

  /**
   * Get all tags
   */
  getTags(): string[] {
    return Array.from(this.taggedHandlers.keys())
  }

  /**
   * Remove tag
   */
  removeTag(tag: string): void {
    const taggedSet = this.taggedHandlers.get(tag)
    if (taggedSet) {
      // Update metadata for all handlers with this tag
      for (const handler of taggedSet) {
        const metadata = this.handlerMetadata.get(handler)
        if (metadata?.tags) {
          metadata.tags = metadata.tags.filter(t => t !== tag)
          if (metadata.tags.length === 0) {
            delete metadata.tags
          }
        }
      }
      
      this.taggedHandlers.delete(tag)
    }
  }

  /**
   * Get handlers with metadata
   */
  getTaggedHandlers(): TaggedHandler[] {
    return this.getHandlers().map(handler => ({
      handler,
      metadata: this.getMetadata(handler) || {},
    }))
  }

  /**
   * Find handlers by criteria
   */
  find(criteria: {
    name?: string
    tags?: string[]
    author?: string
  }): RequestHandler[] {
    const handlers = this.getHandlers()
    
    return handlers.filter(handler => {
      const metadata = this.getMetadata(handler)
      
      if (criteria.name && metadata?.name !== criteria.name) {
        return false
      }
      
      if (criteria.author && metadata?.author !== criteria.author) {
        return false
      }
      
      if (criteria.tags && criteria.tags.length > 0) {
        const handlerTags = metadata?.tags || []
        const hasAllTags = criteria.tags.every(tag => handlerTags.includes(tag))
        if (!hasAllTags) {
          return false
        }
      }
      
      return true
    })
  }

  /**
   * Get registry statistics
   */
  getStats() {
    const handlers = this.getHandlers()
    const tags = this.getTags()
    
    const tagStats = tags.map(tag => ({
      tag,
      count: this.getByTag(tag).length,
    }))
    
    return {
      totalHandlers: handlers.length,
      totalTags: tags.length,
      tagStats,
    }
  }
}

/**
 * Global handler registry instance
 */
export const globalHandlerRegistry = new MockHandlerRegistry()

/**
 * Register handlers globally
 */
export function registerHandlers(...handlers: RequestHandler[]): void {
  globalHandlerRegistry.register(...handlers)
}

/**
 * Unregister handlers globally
 */
export function unregisterHandlers(...handlers: RequestHandler[]): void {
  globalHandlerRegistry.unregister(...handlers)
}

/**
 * Get all registered handlers
 */
export function getRegisteredHandlers(): RequestHandler[] {
  return globalHandlerRegistry.getHandlers()
}

/**
 * Clear all registered handlers
 */
export function clearRegisteredHandlers(): void {
  globalHandlerRegistry.clear()
}

/**
 * Tag handlers globally
 */
export function tagHandlers(tag: string, ...handlers: RequestHandler[]): void {
  globalHandlerRegistry.tag(tag, ...handlers)
}

/**
 * Get handlers by tag
 */
export function getHandlersByTag(tag: string): RequestHandler[] {
  return globalHandlerRegistry.getByTag(tag)
}

/**
 * Create a scoped registry
 */
export function createHandlerRegistry(): MockHandlerRegistry {
  return new MockHandlerRegistry()
}
